import PropTypes from "prop-types"
import React, { useState } from "react"
import { useForm } from "react-hook-form"
import { useMutation } from "react-query"

import Button from "src/components/Button"
import Form from "src/components/Form"

import { createStop, updateStop } from "src/api/CruisePlan/mutateStop"

import { useTracker } from "src/hooks/use_tracker"

import { snakecaseToCamelKeys } from "src/utils/object_helpers"

import DateTimeInput, { dateToLoad, dateToSave } from "./DateTimeInput"
import MarinaStopAutocomplete from "./MarinaStopAutocomplete"
import TravelPlans from "./TravelPlans"

const MarinaStopForm = ({ cruiseStop }) => {
  const [marina, setMarina] = useState(snakecaseToCamelKeys(cruiseStop.marina))
  const tracker = useTracker()

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: cruiseStop.title,
      note: cruiseStop.note,
      startDate: cruiseStop.startDate ? dateToLoad(cruiseStop.startDate) : null,
      endDate: cruiseStop.endDate ? dateToLoad(cruiseStop.endDate) : null,
      nauticalMiles: cruiseStop.nauticalMiles,
      knots: cruiseStop.knots,
    },
  })

  const { mutate } = useMutation({
    mutationFn: (data) => {
      data.start_date = data.startDate ? dateToSave(data.startDate) : null
      data.end_date = data.endDate ? dateToSave(data.endDate) : null
      data.nautical_miles = data.nauticalMiles
      data.mcom_id = marina.mcomId
      if (cruiseStop.id) {
        return updateStop({
          type: cruiseStop.type,
          cruisePlanId: cruiseStop.cruisePlanId,
          id: cruiseStop.id,
          data: data,
        })
      } else {
        return createStop({
          type: cruiseStop.type,
          cruisePlanId: cruiseStop.cruisePlanId,
          data: data,
        })
      }
    },
    onSuccess: (reponse) => {
      tracker.trackEvent("cruise_plans:marina_stops:saved", {
        experience_name: "cruise_plans",
        marina_id: marina.mcomId,
      })
      window.location.href = reponse.redirect_url
    },
    onError: (reponse) => {
      alert(reponse.errors)
    },
  })

  const onSubmit = (data) => {
    mutate(data)
  }

  const renderMarinaLookupForm = () => {
    return <MarinaStopAutocomplete marina={marina} setMarina={setMarina} />
  }

  const renderMarina = () => {
    return (
      <div className="relative mb-4 flex gap-4 rounded border p-4">
        <button
          className="btn-sm absolute right-2 top-0 cursor-pointer bg-white p-2 hover:bg-gray-100"
          onClick={() => setMarina(null)}
        >
          x
        </button>
        <img
          alt={marina.name}
          className="size-18 rounded"
          src={marina.imageUrl}
        />
        <div>
          <h2 className="m-0 text-lg font-semibold">{marina.name}</h2>
          <span className="mt-2">{marina.description}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="container mb-10 max-w-5xl">
      <a
        href={`/account/cruise_plans/${cruiseStop.cruisePlanId}`}
        className="text-link"
      >
        ← Back to cruise plan
      </a>
      <div className="card mt-4">
        <h1 className="my-0 text-xl font-bold">Add a Marina</h1>
        <p className="mb-4">
          Add a marina you are planning to stop, you can later link it with a
          reservation.
        </p>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="space-y-4">
            {!marina ? renderMarinaLookupForm() : renderMarina()}
            <DateTimeInput
              title="Arriving at"
              name="startDate"
              control={control}
              watch={watch}
              setValue={setValue}
              errors={errors}
            />
            <DateTimeInput
              title="Departing at"
              name="endDate"
              control={control}
              watch={watch}
              setValue={setValue}
              errors={errors}
            />
            <div>
              <Form.Label htmlFor="note">Note</Form.Label>
              <Form.Textarea
                id="note"
                {...register(`note`)}
                hasErrors={!!errors?.note}
              />
              {errors?.note && <Form.Error>{errors?.note?.message}</Form.Error>}
            </div>
          </div>

          <TravelPlans
            fromCruiseStopName={cruiseStop.fromCruiseStopName}
            fromCruiseStopEndDate={cruiseStop.fromCruiseStopEndDate}
            register={register}
            errors={errors}
            watch={watch}
          />

          <div className="mt-4">
            <Button variant="primary" type="submit">
              {cruiseStop.id ? "Update Marina Stop" : "Add Marina Stop"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

MarinaStopForm.propTypes = {
  cruiseStop: PropTypes.shape({
    id: PropTypes.string,
    cruisePlanId: PropTypes.string.isRequired,
    marina: PropTypes.object,
    title: PropTypes.string,
    note: PropTypes.string,
    startDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    endDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    nauticalMiles: PropTypes.number,
    knots: PropTypes.number,
    fromCruiseStopName: PropTypes.string,
    fromCruiseStopEndDate: PropTypes.string,
    type: PropTypes.string,
  }).isRequired,
}

export default MarinaStopForm
